import { apiRequest } from "./api";
import { readAllowedReturnPath } from "./auth-return-path";
import type { AccountResponse, Offer } from "./types";

export const LOW_PRICE_REFUND_POLICY = "LOW_PRICE_14_DAY_HALF";
export const NON_REFUNDABLE_POLICY = "NON_REFUNDABLE";
export const PENDING_STOREFRONT_PRODUCT_KEY = "cheemspay-pending-product";

export function isLowPriceOffer(offer: Offer): boolean {
  return offer.refundPolicyCode === LOW_PRICE_REFUND_POLICY;
}

export function refundPolicyConsentText(offer: Offer): string | null {
  if (offer.refundPolicyCode === NON_REFUNDABLE_POLICY) {
    return "我已知悉：Bomana 1 元 3 天试用购买后不退款。";
  }
  if (isLowPriceOffer(offer)) {
    return "我已知悉：14 天内退款，退实付金额的 50%。";
  }
  return null;
}

export function requiresRefundPolicyConsent(offer: Offer): boolean {
  return refundPolicyConsentText(offer) !== null;
}

export function isRefundRequestAvailable(order: {
  orderStatus: string;
  refundPolicyCode: Offer["refundPolicyCode"];
}): boolean {
  return (
    order.orderStatus === "FULFILLED" &&
    order.refundPolicyCode !== NON_REFUNDABLE_POLICY
  );
}

export function isFirstPurchaseAccount(
  account: AccountResponse["account"] | null | undefined,
): boolean {
  return !account?.customer?.recoveryEmail && (account?.entitlements.length ?? 0) === 0;
}

export function rememberPendingStorefrontProduct(
  productId: string,
  storage: Pick<Storage, "setItem">,
): void {
  storage.setItem(PENDING_STOREFRONT_PRODUCT_KEY, productId);
}

export function takePendingStorefrontProduct(
  storage: Pick<Storage, "getItem" | "removeItem">,
): string {
  const productId = storage.getItem(PENDING_STOREFRONT_PRODUCT_KEY) ?? "";
  if (productId) {
    storage.removeItem(PENDING_STOREFRONT_PRODUCT_KEY);
  }
  return productId;
}

export function storefrontReturnPath(search: string, origin: string): string {
  const raw = new URLSearchParams(search).get("returnTo");
  if (!raw) {
    return "/";
  }
  return readAllowedReturnPath(raw, origin);
}

export async function createCheemsPayOrder(input: {
  productId: string;
  refundPolicyAccepted: boolean;
  pendingRecoveryEmail?: string | null;
  referralCode?: string | null;
}): Promise<{ orderId: string; paymentUrl: string }> {
  return apiRequest("/api/orders", {
    method: "POST",
    body: JSON.stringify({
      productId: input.productId,
      refundPolicyAccepted: input.refundPolicyAccepted,
      pendingRecoveryEmail: input.pendingRecoveryEmail || null,
      referralCode: input.referralCode?.trim() || null,
    }),
  });
}
